import { Tag, Tooltip } from 'antd';
import type { OrgRole } from '../types';
import { ORG_ROLE_LABEL } from '../types';

/** The user's position in their own company, `users.org_role`.
 *
 * Worth its own column: the org-level gates (create a workspace, invite people,
 * edit org settings) read this column and nothing else, so it is the one that
 * answers "why can't they invite anyone?". Two cases it makes visible:
 *
 *  * `null` — nothing org-wide is open to this user, however their workspace
 *    memberships read.
 */
export function OrgRoleTag({ orgRole }: { orgRole: string | null }) {
  if (!orgRole) {
    return (
      <Tooltip title="users.org_role is null. The org-level gates read this column, so nothing org-wide is open to this user until it is set.">
        <Tag color="orange">Not set</Tag>
      </Tooltip>
    );
  }

  // No "custom" marker any more: there is no second account-wide role left for
  // this column to disagree with. What used to look like a divergence was
  // `users.role_type` left stale, and AD-15 dropped that column.
  return (
    <Tag color={orgRole === 'org_admin' ? 'geekblue' : 'default'}>
      {ORG_ROLE_LABEL[orgRole as OrgRole] ?? orgRole}
    </Tag>
  );
}

export function StatusTag({ status, isActive }: { status: string | null; isActive: boolean }) {
  if (status === 'active') return <Tag color="green">Active</Tag>;
  if (status === 'pending') return <Tag color="orange">Pending</Tag>;
  if (status === 'deactive') return <Tag color="red">Deactivated</Tag>;
  // status is nullable in the schema; fall back to the boolean that gates login.
  return <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>;
}

/** Seiki staff elevation — the one access fact still held on the user row.
 *
 * The per-user capability tags that used to sit here are gone: upload/delete
 * inventory are workspace-scoped grants now, so a tag on a cross-org user table
 * could only ever be wrong about where they apply. The user drawer lists the
 * real grants, workspace by workspace.
 */
export function StaffTag({ isSeikiStaff }: { isSeikiStaff: boolean }) {
  if (!isSeikiStaff) {
    return <span style={{ color: '#9ca3af' }}>&mdash;</span>;
  }

  return (
    <Tooltip title="Seiki staff (users.is_seiki_staff) — hard delete and KPI recalculation across all orgs. Additive: it does not change either role beside it.">
      <Tag color="purple">Seiki staff</Tag>
    </Tooltip>
  );
}
